import React, { useState, useEffect } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  getBillingExceptions,
  createBillingException,
  updateBillingException,
  deactivateBillingException,
} from "../../../../../../api/macLeader";

const FTE_OPTIONS = [0, 0.25, 0.5, 0.75, 1.0];

const lastDayOf = (ym) => {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(y, m, 0); // last day of the month in local time
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
};

const fmt = (d) => {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const EmptyForm = () => ({
  startDate: "",
  endDate: "",
  fte: null,
  reason: "",
});

const BillingExceptionsPanel = ({ requestId, readOnly, positionType }) => {
  const theme = useTheme();
  const t = theme.custom;
  const isDark = theme.palette.mode === "dark";

  const [exceptions, setExceptions]   = useState([]);
  const [loading, setLoading]         = useState(false);
  const [showForm, setShowForm]       = useState(false);
  const [editingId, setEditingId]     = useState(null);
  const [form, setForm]               = useState(EmptyForm());
  const [submitting, setSubmitting]   = useState(false);
  const [deletingId, setDeletingId]   = useState(null);
  const [apiError, setApiError]       = useState(null);

  const fetchExceptions = async () => {
    if (!requestId) return;
    try {
      setLoading(true);
      const data = await getBillingExceptions(requestId);
      setExceptions(data ?? []);
    } catch {
      setExceptions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExceptions();
  }, [requestId]);

  const resetForm = () => {
    setForm(EmptyForm());
    setEditingId(null);
    setApiError(null);
    setShowForm(false);
  };

  const handleEdit = (ex) => {
    setForm({
      startDate: ex.startDate ? ex.startDate.slice(0, 7) : "",
      endDate:   ex.endDate   ? ex.endDate.slice(0, 7)   : "",
      fte:       ex.fte ?? null,
      reason:    ex.reason ?? "",
    });
    setEditingId(ex.billingExceptionId);
    setShowForm(true);
    setApiError(null);
  };

  const handleDelete = async (id) => {
    try {
      setDeletingId(id);
      await deactivateBillingException(id);
      await fetchExceptions();
    } catch (err) {
      setApiError(err?.response?.data?.error || "Failed to remove exception.");
    } finally {
      setDeletingId(null);
    }
  };

  const handleSubmit = async () => {
    if (!form.startDate || !form.endDate || form.fte === null || !form.reason.trim()) {
      setApiError("All fields are required.");
      return;
    }
    const startDate = `${form.startDate}-01`;
    const endDate   = lastDayOf(form.endDate);
    try {
      setSubmitting(true);
      setApiError(null);
      if (editingId) {
        await updateBillingException(editingId, {
          startDate,
          endDate,
          fte:    form.fte,
          reason: form.reason.trim(),
        });
      } else {
        await createBillingException({
          requestId,
          startDate,
          endDate,
          fte:    form.fte,
          reason: form.reason.trim(),
        });
      }
      resetForm();
      await fetchExceptions();
    } catch (err) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || "Something went wrong.";
      setApiError(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = {
    width: "100%", padding: "7px 10px", borderRadius: "3px", boxSizing: "border-box",
    border: `1px solid ${t.border2}`, background: t.surface2,
    color: t.text, fontFamily: "'DM Mono', monospace", fontSize: "12px",
    outline: "none", colorScheme: isDark ? "dark" : "light",
  };

  return (
    <Box>

      {/* Header row */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "10px" }}>
        <Typography sx={{
          fontFamily: "'DM Mono', monospace", fontSize: "8px",
          letterSpacing: "0.15em", textTransform: "uppercase", color: t.text3,
        }}>
          Billing Exceptions
        </Typography>
        {!readOnly && !showForm && positionType !== "UBL" && (
          <Box
            onClick={() => { setShowForm(true); setEditingId(null); setForm(EmptyForm()); setApiError(null); }}
            sx={{
              display: "inline-flex", alignItems: "center", gap: "4px",
              padding: "3px 10px", borderRadius: "3px", cursor: "pointer",
              border: `1px solid ${t.accent}55`, background: t.accent2,
              fontFamily: "'DM Mono', monospace", fontSize: "9px",
              color: t.accent, fontWeight: 600, transition: "all 0.15s",
              "&:hover": { background: t.accent, color: isDark ? "#060E0D" : "#fff" },
            }}
          >
            + Add Exception
          </Box>
        )}
        {positionType === "UBL" && (
          <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "9px", color: t.text3, letterSpacing: "0.05em" }}>
            Not applicable for unbilled allocations
          </Typography>
        )}
      </Box>

      {/* Add / Edit form */}
      {showForm && (
        <Box sx={{
          mb: "12px", padding: "12px 14px",
          background: t.surface2, border: `1px solid ${t.border2}`,
          borderRadius: "4px",
        }}>
          <Typography sx={{
            fontFamily: "'DM Mono', monospace", fontSize: "9px",
            color: t.accent, fontWeight: 600, mb: "10px", letterSpacing: "0.06em",
          }}>
            {editingId ? "Edit Exception" : "New Exception"}
          </Typography>

          {/* Month row */}
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", mb: "8px" }}>
            <Box>
              <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "8px", color: t.text3, mb: "4px", letterSpacing: "0.1em", textTransform: "uppercase" }}>
                Start Month *
              </Typography>
              <input
                type="month"
                value={form.startDate}
                onChange={(e) => setForm((p) => ({ ...p, startDate: e.target.value }))}
                style={inputStyle}
              />
            </Box>
            <Box>
              <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "8px", color: t.text3, mb: "4px", letterSpacing: "0.1em", textTransform: "uppercase" }}>
                End Month *
              </Typography>
              <input
                type="month"
                value={form.endDate}
                onChange={(e) => setForm((p) => ({ ...p, endDate: e.target.value }))}
                style={inputStyle}
              />
            </Box>
          </Box>

          {/* FTE */}
          <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "8px", color: t.text3, mb: "2px", letterSpacing: "0.1em", textTransform: "uppercase" }}>
            Actual Billed FTE *
          </Typography>
          <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "9px", color: t.text3, mb: "6px" }}>
            Enter the actual FTE that will get billed this month
          </Typography>
          <Box sx={{ display: "flex", gap: "6px", mb: "8px", flexWrap: "wrap" }}>
            {FTE_OPTIONS.map((val) => (
              <Box
                key={val}
                onClick={() => setForm((p) => ({ ...p, fte: val }))}
                sx={{
                  padding: "4px 14px", borderRadius: "3px", cursor: "pointer",
                  border: form.fte === val ? `1px solid ${t.accent}88` : `1px solid ${t.border2}`,
                  background: form.fte === val ? t.accent2 : t.surface,
                  fontFamily: "'DM Mono', monospace", fontSize: "11px", fontWeight: 600,
                  color: form.fte === val ? t.accent : t.text2, transition: "all 0.15s",
                }}
              >
                {val}
              </Box>
            ))}
          </Box>

          {/* Reason */}
          <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "8px", color: t.text3, mb: "4px", letterSpacing: "0.1em", textTransform: "uppercase" }}>
            Reason *
          </Typography>
          <textarea
            value={form.reason}
            onChange={(e) => setForm((p) => ({ ...p, reason: e.target.value }))}
            placeholder="e.g. Annual leave, Public holiday, Absence..."
            rows={2}
            style={{
              ...inputStyle,
              fontFamily: "'Inter', sans-serif",
              resize: "vertical",
            }}
          />

          {/* Error */}
          {apiError && (
            <Box sx={{ mt: "8px", padding: "6px 10px", background: "rgba(200,64,64,0.08)", border: "1px solid rgba(200,64,64,0.3)", borderRadius: "3px" }}>
              <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: "#c84040" }}>
                {apiError}
              </Typography>
            </Box>
          )}

          {/* Form actions */}
          <Box sx={{ display: "flex", gap: "8px", mt: "10px" }}>
            <Box
              onClick={resetForm}
              sx={{
                padding: "6px 14px", borderRadius: "3px", cursor: "pointer",
                border: `1px solid ${t.border2}`, fontFamily: "'DM Mono', monospace",
                fontSize: "10px", color: t.text2, transition: "all 0.15s",
                "&:hover": { borderColor: t.border3, color: t.text },
              }}
            >
              Cancel
            </Box>
            <Box
              onClick={submitting ? undefined : handleSubmit}
              sx={{
                flex: 1, padding: "6px 14px", borderRadius: "3px",
                cursor: submitting ? "not-allowed" : "pointer",
                background: submitting ? t.surface3 : t.accent,
                fontFamily: "'DM Mono', monospace", fontSize: "10px", fontWeight: 600,
                color: submitting ? t.text3 : (isDark ? "#060E0D" : "#fff"),
                display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
                transition: "all 0.15s", "&:hover": submitting ? {} : { opacity: 0.88 },
              }}
            >
              {submitting && <CircularProgress size={10} sx={{ color: "inherit" }} />}
              {submitting ? "Saving..." : editingId ? "Save Changes" : "Add Exception"}
            </Box>
          </Box>
        </Box>
      )}

      {/* List */}
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: "16px" }}>
          <CircularProgress size={16} sx={{ color: t.accent }} />
        </Box>
      )}

      {!loading && exceptions.length === 0 && !showForm && (
        <Box sx={{ padding: "12px 14px", background: t.surface2, border: `1px solid ${t.border}`, borderRadius: "3px" }}>
          <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: t.text3 }}>
            No billing exceptions recorded for this allocation.
          </Typography>
        </Box>
      )}

      {!loading && exceptions.map((ex) => (
        <Box
          key={ex.billingExceptionId}
          sx={{
            mb: "6px", padding: "10px 12px",
            background: t.surface2, border: `1px solid ${t.border}`,
            borderLeft: "3px solid rgba(200,138,58,0.6)",
            borderRadius: "3px",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: "4px" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Box sx={{
                padding: "1px 7px", borderRadius: "2px",
                background: "rgba(200,138,58,0.12)", border: "1px solid rgba(200,138,58,0.3)",
              }}>
                <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "9px", fontWeight: 600, color: "#c88a3a" }}>
                  {ex.fte} FTE
                </Typography>
              </Box>
              <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "9px", color: t.text3 }}>
                {fmt(ex.startDate)} → {fmt(ex.endDate)}
              </Typography>
            </Box>

            {!readOnly && (
              <Box sx={{ display: "flex", gap: "6px", flexShrink: 0 }}>
                <Box
                  onClick={() => handleEdit(ex)}
                  sx={{
                    padding: "2px 8px", borderRadius: "2px", cursor: "pointer",
                    border: `1px solid ${t.border2}`, fontFamily: "'DM Mono', monospace",
                    fontSize: "9px", color: t.text2, transition: "all 0.15s",
                    "&:hover": { borderColor: t.accent, color: t.accent },
                  }}
                >
                  Edit
                </Box>
                <Box
                  onClick={() => deletingId !== ex.billingExceptionId && handleDelete(ex.billingExceptionId)}
                  sx={{
                    padding: "2px 8px", borderRadius: "2px", cursor: "pointer",
                    border: "1px solid rgba(200,64,64,0.25)",
                    background: "rgba(200,64,64,0.06)",
                    fontFamily: "'DM Mono', monospace", fontSize: "9px", color: "#c84040",
                    transition: "all 0.15s",
                    "&:hover": { background: "rgba(200,64,64,0.14)" },
                    opacity: deletingId === ex.billingExceptionId ? 0.5 : 1,
                  }}
                >
                  {deletingId === ex.billingExceptionId ? "..." : "Remove"}
                </Box>
              </Box>
            )}
          </Box>

          <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "11px", color: t.text2, lineHeight: 1.5 }}>
            {ex.reason}
          </Typography>

          <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "8px", color: t.text3, mt: "4px" }}>
            Added by {ex.createdByName ?? "—"}
            {ex.updatedAt ? " · Edited" : ""}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export default BillingExceptionsPanel;